import { useAppSelector } from '../../../app/hooks';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';

import * as dagre from 'dagre';

import styles from './OntologyDiagram.module.scss';
import React from 'react';

interface TpAssetNode {
  id: string;
  displayName: string;
  xPos: number;
  yPos: number;
  width: number;
  height: number;
}

interface TpAssetRelation {
  id: string;
  node1: TpAssetNode;
  node2: TpAssetNode;
  displayNameForward: string;
  displayNameReverse: string;
}

function getEdgePath(rel: TpAssetRelation): string {
  const node1 = rel.node1;
  const node2 = rel.node2;
  const offs = 170;
  const startX = node1.xPos + node1.width - 100;
  const startY = node1.yPos + node1.height / 2;
  const endX = node2.xPos + 100;
  const endY = node2.yPos + node2.height / 2;
  return `M ${startX},${startY} C ${startX + offs},${startY} ${endX - offs},${endY} ${endX},${endY}`;
}

function renderRelationNames(rel: TpAssetRelation, idx: number): any {
  if (rel.node1.xPos < rel.node2.xPos) {
    const vDiff = rel.node2.yPos - rel.node1.yPos;
    const lineH = 12;
    return (
      <React.Fragment key={`${rel.id}_${idx}`}>
        <div
          className={styles.relNameLeft}
          style={{
            left: `${rel.node1.xPos + rel.node1.width + 3}px`,
            top: `${rel.node1.yPos + rel.node1.height / 2 - 1 + vDiff * 0.18 - (vDiff > 0 ? lineH : 0)}px`,
          }}
        >
          {rel.displayNameForward}&rarr;
        </div>
        <div
          className={styles.relNameRight}
          style={{
            left: `${rel.node2.xPos - 3}px`,
            top: `${rel.node2.yPos + rel.node2.height / 2 - 1 - vDiff * 0.18 - (vDiff <= 0 ? lineH : 0)}px`,
          }}
        >
          &larr;{rel.displayNameReverse}
        </div>
      </React.Fragment>
    );
  }
}

export function OntologyDiagam() {
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const assetTypesMap = ontology.assetTypes;

  const assetNodes: TpAssetNode[] = [];
  const assetNodesMap: { [id: string]: TpAssetNode } = {};
  const assetRelations: TpAssetRelation[] = [];

  const nodeSizeY = 90;
  const nodeSizeX = 180;

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'LR', //  left to right layout
    nodesep: nodeSizeY, // vertical separation
    edgesep: 0,
    ranksep: nodeSizeX + 50, // horizontal separation
    ranker: 'tight-tree', //'network-simplex'
  });
  g.setDefaultEdgeLabel(function () {
    return {};
  });

  // Add assets as nodes
  for (const assetTypeId of Object.keys(assetTypesMap)) {
    const assetType = assetTypesMap[assetTypeId];
    g.setNode(assetTypeId, { width: 60, height: 30 });
    const node = {
      id: assetTypeId,
      displayName: assetType.displayName,
      xPos: 0,
      yPos: 0,
      width: nodeSizeX,
      height: nodeSizeY,
    };
    assetNodes.push(node);
    assetNodesMap[assetTypeId] = node;
  }

  // Add relations as edges
  for (const assetTypeId of Object.keys(assetTypesMap)) {
    const assetType = assetTypesMap[assetTypeId];
    for (const rel of assetType.relations) {
      if (Object.keys(assetTypesMap).includes(rel.toAsset)) {
        g.setEdge(assetTypeId, rel.toAsset);
        assetRelations.push({
          id: rel.id,
          node1: assetNodesMap[assetTypeId],
          node2: assetNodesMap[rel.toAsset],
          displayNameForward: rel.displayNameForward,
          displayNameReverse: rel.displayNameReverse,
        });
      }
    }
  }

  dagre.layout(g);

  let totWidth = 1;
  let totHeight = 1;
  for (const assetNode of assetNodes) {
    assetNode.xPos = g.node(assetNode.id).x;
    assetNode.yPos = g.node(assetNode.id).y;
    totWidth = Math.max(totWidth, assetNode.xPos + nodeSizeX);
    totHeight = Math.max(totHeight, assetNode.yPos + nodeSizeY);
  }
  totWidth += 20;
  totHeight += 20;

  return (
    <div className={styles.wrapper}>
      <div style={{ width: `${totWidth}px`, height: `${totHeight}px` }}>
        <svg style={{ position: 'absolute', width: `${totWidth}px`, height: `${totHeight}px` }}>
          {assetRelations.map((rel, idx) => (
            <React.Fragment key={`edge_${idx}`}>
              <path className={styles.relPathBg} d={getEdgePath(rel)} />
              <path className={styles.relPath} d={getEdgePath(rel)} />
            </React.Fragment>
          ))}
        </svg>
        {assetNodes.map((assetNode) => (
          <div
            key={assetNode.id}
            className={styles.assetNode}
            style={{
              left: `${assetNode.xPos}px`,
              top: `${assetNode.yPos}px`,
              width: `${assetNode.width}px`,
              height: `${assetNode.height}px`,
            }}
            onClick={() => {}}
          >
            <div className={styles.identifier}>{assetNode.id}</div>
            <b>{assetNode.displayName}</b>
          </div>
        ))}
        {assetRelations.map((rel, idx) => renderRelationNames(rel, idx))}
      </div>
    </div>
  );
}
